import {
  Building2,
  CloudRain,
  Clock3,
  Map as MapIcon,
  MapPin,
  TicketCheck,
  Trees,
} from "lucide-react";
import { categoryById } from "../data/categories";
import { regionById } from "../data/regions";
import type { FilterState, Place } from "../data/schema";

const capitalRegions = ["seoul", "gyeonggi", "incheon"] as const;
type CapitalRegionId = (typeof capitalRegions)[number];

const regionMeta: Record<CapitalRegionId, { eyebrow: string; description: string }> = {
  seoul: {
    eyebrow: "도심 체험",
    description: "대중교통으로 가볍게 만나는 박물관·과학·공원",
  },
  gyeonggi: {
    eyebrow: "하루 나들이",
    description: "넓은 공간에서 오래 즐기는 체험·테마 코스",
  },
  incheon: {
    eyebrow: "바다와 도시",
    description: "교통·생태·해양 경험을 묶는 서부권 코스",
  },
};

interface CapitalAreaExplorerProps {
  places: Place[];
  filters: FilterState;
  onChange: (filters: FilterState) => void;
  onShowMap: () => void;
}

export function CapitalAreaExplorer({
  places,
  filters,
  onChange,
  onShowMap,
}: CapitalAreaExplorerProps) {
  const capitalPlaces = places.filter((place) => isCapitalRegion(place.region));
  const selectedRegion = filters.regions.find(isCapitalRegion);
  const districtSource = selectedRegion
    ? capitalPlaces.filter((place) => place.region === selectedRegion)
    : capitalPlaces;
  const districts = countValues(districtSource.map((place) => place.district)).slice(0, 8);

  return (
    <section className="capital-explorer" aria-labelledby="capital-explorer-title">
      <div className="capital-explorer-heading">
        <div>

          <h2 id="capital-explorer-title">수도권</h2>
          <p>서울·경기·인천에서 찾아보세요.</p>
        </div>
        <button className="capital-map-button" onClick={onShowMap} type="button">
          <MapIcon size={16} aria-hidden="true" />
          수도권 지도 보기
        </button>
      </div>

      <div className="capital-region-grid">
        {capitalRegions.map((region) => {
          const regionalPlaces = capitalPlaces.filter((place) => place.region === region);
          const districtCount = new Set(regionalPlaces.map((place) => place.district)).size;
          const topCategories = countValues(regionalPlaces.map((place) => place.category))
            .slice(0, 2)
            .map(([category]) => categoryById[category].shortName)
            .join(" · ");
          const active = filters.regions.includes(region);
          const meta = regionMeta[region];

          return (
            <button
              aria-pressed={active}
              className={active ? "active" : ""}
              disabled={regionalPlaces.length === 0}
              key={region}
              onClick={() =>
                onChange({
                  ...filters,
                  areaGroups: ["capital"],
                  regions: active ? [] : [region],
                  districts: [],
                })
              }
              type="button"
            >
              <span className="capital-region-icon">
                {region === "seoul" ? (
                  <Building2 size={20} aria-hidden="true" />
                ) : region === "gyeonggi" ? (
                  <Trees size={20} aria-hidden="true" />
                ) : (
                  <MapPin size={20} aria-hidden="true" />
                )}
              </span>
              <span className="capital-region-copy">
                <small>{meta.eyebrow}</small>
                <strong>{regionById[region].name}</strong>
                <span>{regionalPlaces.length ? meta.description : "여행지 준비 중"}</span>
              </span>
              <span className="capital-region-stats">
                <b>{regionalPlaces.length}곳</b>
                <small>{districtCount ? `${districtCount}개 시·군·구` : "준비 중"}</small>
                {topCategories && <small>{topCategories}</small>}
              </span>
            </button>
          );
        })}
      </div>

      <div className="capital-quick-row">
        <div className="capital-quick-filters" aria-label="수도권 빠른 조건">
          <button
            className={filters.rainFriendlyOnly ? "active" : ""}
            onClick={() =>
              onChange({ ...filters, rainFriendlyOnly: !filters.rainFriendlyOnly })
            }
            type="button"
          >
            <CloudRain size={15} aria-hidden="true" />
            비 오는 날
          </button>
          <button
            className={filters.priceType === "free" ? "active" : ""}
            onClick={() =>
              onChange({
                ...filters,
                priceType: filters.priceType === "free" ? "all" : "free",
              })
            }
            type="button"
          >
            <TicketCheck size={15} aria-hidden="true" />
            무료 나들이
          </button>
          <button
            className={filters.stayTimes.includes("half-day") ? "active" : ""}
            onClick={() =>
              onChange({
                ...filters,
                stayTimes: filters.stayTimes.includes("half-day") ? [] : ["half-day"],
              })
            }
            type="button"
          >
            <Clock3 size={15} aria-hidden="true" />
            반나절 코스
          </button>
        </div>

        {districts.length > 0 && (
          <div className="capital-districts" aria-label="수도권 주요 시군구">
            <span>주요 지역</span>
            {districts.map(([district, count]) => (
              <button
                className={filters.districts.includes(district) ? "active" : ""}
                key={district}
                onClick={() =>
                  onChange({
                    ...filters,
                    districts: filters.districts.includes(district) ? [] : [district],
                  })
                }
                type="button"
              >
                {district}
                <small>{count}</small>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function countValues<T extends string>(values: T[]) {
  const counts = values.reduce(
    (result, value) => {
      result.set(value, (result.get(value) ?? 0) + 1);
      return result;
    },
    new Map<T, number>(),
  );

  return Array.from(counts.entries()).sort(
    ([a, countA], [b, countB]) => countB - countA || a.localeCompare(b, "ko"),
  );
}

function isCapitalRegion(region: string): region is CapitalRegionId {
  return capitalRegions.some((capitalRegion) => capitalRegion === region);
}
